import React, { useEffect, useState, useRef, useMemo } from "react";
import { motion } from "framer-motion";
import { Plus, Minus, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useLocation, useNavigate } from "react-router-dom";
import { useContent } from "../context/ContentContext";
import { useExtras } from "../context/ExtrasContext";
import PageHero from "../components/PageHero";
import { cropStyle } from "../lib/cropStyle";
import { basisLabel } from "../lib/pricing";

export default function Gallery() {
  const { content } = useContent();
  const location = useLocation();
  const navigate = useNavigate();
  const extras = useExtras();
  const [media, setMedia] = useState([]);
  const [cats, setCats] = useState([]);
  const [album, setAlbum] = useState("");
  const [sub, setSub] = useState("");
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    api.get("/gallery").then((r) => setMedia(r.data)).catch(() => {});
    api.get("/media-categories").then((r) => setCats(r.data)).catch(() => {});
  }, []);

  const defaultCat = content?.gallery_default_category || "";
  const albums = useMemo(() => {
    const subNames = cats.filter((c) => c.parent).map((c) => c.name);
    const topNames = cats.filter((c) => !c.parent).map((c) => c.name);
    const usedAlbums = Array.from(new Set(media.map((m) => m.album)));
    let ordered = [...topNames.filter((n) => usedAlbums.includes(n)), ...usedAlbums.filter((n) => !cats.some((c) => c.name === n))];
    ordered = ordered.filter((n) => !subNames.includes(n));
    if (defaultCat && ordered.includes(defaultCat)) ordered = [defaultCat, ...ordered.filter((n) => n !== defaultCat)];
    return ordered;
  }, [cats, media, defaultCat]);
  const userPicked = useRef(false);

  useEffect(() => {
    if (!albums.length || userPicked.current) return;
    setAlbum(defaultCat && albums.includes(defaultCat) ? defaultCat : albums[0]);
  }, [albums, defaultCat]);

  useEffect(() => {
    const q = new URLSearchParams(location.search);
    const cat = q.get("cat"); const subParam = q.get("sub");
    if (cat && albums.includes(cat)) { userPicked.current = true; setAlbum(cat); setSub(subParam || ""); }
  }, [albums, location.search]);

  const subs = cats.filter((c) => c.parent === album);
  const filtered = media.filter((m) => (album ? m.album === album : true) && (sub ? m.subcategory === sub : true));
  const ph = content?.page_heroes?.gallery || {};
  const originParams = new URLSearchParams(location.search);
  const originExp = originParams.get("exp");
  const originFac = originParams.get("fac");
  const [expName, setExpName] = useState("");
  useEffect(() => {
    if (!originExp) { setExpName(""); return; }
    api.get("/experiences").then((r) => setExpName(r.data.find((e) => e.id === originExp)?.title || "")).catch(() => {});
  }, [originExp]);
  const facName = originFac ? ((content?.facilities || []).find((f) => f.id === originFac)?.label || "") : "";
  const returnName = originExp ? expName : facName;

  return (
    <div>
      <PageHero
        eyebrow={ph.eyebrow || "Gallery"}
        title={ph.title || "A visual journey"}
        subtitle={ph.subtitle || "Explore our suites, interiors, grounds and the wild beauty that surrounds them."}
        image={ph.image || content?.hero_images?.[1] || media[0]?.url}
      />
      <section className="py-16 container-lux">
        {(originExp || originFac) && (
          <button
            onClick={() => navigate(originExp ? `/experiences?exp=${originExp}` : `/facilities/${originFac}`)}
            data-testid="gallery-return"
            className="mb-6 inline-flex items-center gap-2 rounded-full bg-forest text-cream px-5 py-2.5 text-sm font-semibold hover:bg-forest/90 transition-colors"
          >
            <ArrowLeft size={16} /> Return to {returnName || (originExp ? "Experience" : "Facility")}
          </button>
        )}
        <div className="flex flex-wrap gap-3 mb-4">
          {albums.map((a) => (
            <button
              key={a} data-testid={`gallery-filter-${a}`} onClick={() => { userPicked.current = true; setAlbum(a); setSub(""); }}
              className={`px-5 py-2 rounded-full text-sm font-medium transition-colors duration-200 ${
                album === a ? "bg-rose text-white" : "bg-white border border-stone-200 text-stone-600 hover:border-rose/40"
              }`}
            >
              {a}
            </button>
          ))}
        </div>
        {subs.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-8" data-testid="gallery-subfilters">
            {["All", ...subs.map((c) => c.name)].map((s) => {
              const active = (s === "All" && !sub) || sub === s;
              return (
                <button key={s} data-testid={`gallery-subfilter-${s}`} onClick={() => { userPicked.current = true; setSub(s === "All" ? "" : s); }}
                  className={`px-4 py-1.5 rounded-full text-xs font-medium transition-colors ${active ? "bg-forest text-cream" : "bg-white border border-stone-200 text-stone-500 hover:border-forest/40"}`}>
                  {s}
                </button>
              );
            })}
          </div>
        )}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {filtered.map((m, i) => (
            <motion.div
              key={m.id} initial={{ opacity: 0, scale: 0.96 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }}
              transition={{ delay: (i % 6) * 0.05 }}
              className="aspect-square rounded-2xl overflow-hidden cursor-pointer group relative"
              onClick={() => setLightbox(m)} data-testid={`gallery-item-${m.id}`}
            >
              <img src={m.url} alt={m.title} className="h-full w-full object-cover object-center group-hover:scale-105 transition-transform duration-500" style={cropStyle(m.position, m.zoom)} />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-4">
                <div><div className="text-cream font-serif text-lg">{m.title}</div><div className="text-cream/70 text-xs">{m.album}</div></div>
              </div>
              {m.bookable && m.price > 0 && (
                <div className="absolute bottom-0 inset-x-0 p-2 bg-gradient-to-t from-black/75 to-transparent flex items-center justify-between gap-2" onClick={(e) => e.stopPropagation()}>
                  <span className="text-white text-sm font-semibold" data-testid={`gallery-price-${m.id}`}>${m.price}<span className="text-[10px] font-normal opacity-80"> {basisLabel(m.price_basis)}{m.max_guests ? ` · up to ${m.max_guests}` : ""}</span></span>
                  {m.sold_out ? (
                    <span data-testid={`gallery-soldout-${m.id}`} className="text-xs font-semibold rounded-full bg-stone-700 text-white px-3 py-1.5">Sold out</span>
                  ) : (extras.items[m.id] || 0) === 0 ? (
                    <button onClick={() => { extras.add(m.id); toast.success(`${m.title || "Experience"} added to your reservation`); }} data-testid={`gallery-add-${m.id}`} className="text-xs font-semibold rounded-full bg-rose text-white px-3 py-1.5 hover:bg-rose/90 transition-colors">Add +</button>
                  ) : (
                    <div className="inline-flex items-center gap-1.5 bg-white/95 rounded-full px-1.5 py-1">
                      <button onClick={() => extras.dec(m.id)} data-testid={`gallery-dec-${m.id}`} className="h-5 w-5 rounded-full bg-stone-100 flex items-center justify-center"><Minus size={11} /></button>
                      <span className="text-xs font-semibold w-4 text-center" data-testid={`gallery-qty-${m.id}`}>{extras.items[m.id]}</span>
                      <button onClick={() => extras.inc(m.id)} data-testid={`gallery-inc-${m.id}`} className="h-5 w-5 rounded-full bg-rose text-white flex items-center justify-center"><Plus size={11} /></button>
                    </div>
                  )}
                </div>
              )}
            </motion.div>
          ))}
        </div>
      </section>

      {lightbox && (
        <div className="fixed inset-0 z-[60] bg-black/90 flex items-center justify-center p-6" onClick={() => setLightbox(null)} data-testid="gallery-lightbox">
          <img src={lightbox.url} alt={lightbox.title} className="max-h-[85vh] max-w-full rounded-lg object-contain" />
        </div>
      )}
    </div>
  );
}
